import { validateWalletTextForRecovery, WalletIdentityInfo } from './bsv-signing'

export interface WalletRecoveryIdentity extends WalletIdentityInfo {
    userId: string
}

export interface WalletPasskeyEnvelope extends WalletRecoveryIdentity {
    version: 1
    algorithm: 'AES-256-GCM'
    kdf: 'HKDF-SHA-256'
    network: 'mainnet'
    walletGeneration: string
    credentialId: string
    prfSalt: string
    hkdfSalt: string
    walletNonce: string
    wrappedKeyNonce: string
    ciphertext: string
    wrappedKey: string
}

const WALLET_MAX_BYTES = 65536
const PRF_BYTES = 32
const NONCE_BYTES = 12
const TAG_BYTES = 16
const WRAP_CONTEXT = 'treechat-wallet-recovery:wrap:v1'
const WALLET_CONTEXT = 'treechat-wallet-recovery:wallet:v1'
const encoder = new TextEncoder()
const decoder = new TextDecoder('utf-8', { fatal: true })
const ENVELOPE_FIELDS = [
    'version',
    'algorithm',
    'kdf',
    'network',
    'userId',
    'paymentAddress',
    'ordinalsAddress',
    'walletGeneration',
    'credentialId',
    'prfSalt',
    'hkdfSalt',
    'walletNonce',
    'wrappedKeyNonce',
    'ciphertext',
    'wrappedKey',
]

function webCrypto(): Crypto {
    if (!globalThis.crypto?.subtle) {
        throw new Error('Web Crypto is not available in this browser')
    }
    return globalThis.crypto
}

export function encodeBase64Url(bytes: Uint8Array): string {
    let binary = ''
    for (const byte of bytes) binary += String.fromCharCode(byte)
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '')
}

/** Canonical, unpadded base64url only; limits are checked before allocating. */
export function decodeBase64Url(
    value: unknown,
    minBytes = 1,
    maxBytes = WALLET_MAX_BYTES + TAG_BYTES,
): Uint8Array {
    if (
        typeof value !== 'string' ||
        value.length > Math.ceil((maxBytes * 4) / 3) ||
        !/^[A-Za-z0-9_-]+$/u.test(value) ||
        value.length % 4 === 1
    ) {
        throw new Error('Invalid recovery base64url value')
    }
    const binary = atob(
        value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4),
    )
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0))
    if (bytes.length < minBytes || bytes.length > maxBytes || encodeBase64Url(bytes) !== value) {
        throw new Error('Invalid recovery base64url size or encoding')
    }
    return bytes
}

export function createWalletPasskeyPrfSalt(): string {
    return encodeBase64Url(webCrypto().getRandomValues(new Uint8Array(PRF_BYTES)))
}

function assertIdentity(identity: WalletRecoveryIdentity): void {
    if (
        !identity ||
        typeof identity.userId !== 'string' ||
        !/^[A-Za-z0-9_-]{1,128}$/u.test(identity.userId) ||
        typeof identity.paymentAddress !== 'string' ||
        typeof identity.ordinalsAddress !== 'string' ||
        !/^[1-9A-HJ-NP-Za-km-z]{26,35}$/u.test(identity.paymentAddress) ||
        !/^[1-9A-HJ-NP-Za-km-z]{26,35}$/u.test(identity.ordinalsAddress)
    ) {
        throw new Error('Invalid recovery wallet identity')
    }
}

export async function walletRecoveryGeneration(identity: WalletIdentityInfo): Promise<string> {
    const digest = await webCrypto().subtle.digest(
        'SHA-256',
        encoder.encode(`${identity.paymentAddress}:${identity.ordinalsAddress}`),
    )
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}

/** Reject unsupported formats before asking Web Crypto to process them. */
export async function validateWalletPasskeyEnvelope(
    value: unknown,
    expectedIdentity?: WalletRecoveryIdentity,
    expectedCredentialId?: string,
): Promise<WalletPasskeyEnvelope> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('Invalid recovery envelope')
    }
    const envelope = value as WalletPasskeyEnvelope
    const fields = Object.keys(envelope)
    if (
        fields.length !== ENVELOPE_FIELDS.length ||
        fields.some(field => !ENVELOPE_FIELDS.includes(field)) ||
        envelope.version !== 1 ||
        envelope.algorithm !== 'AES-256-GCM' ||
        envelope.kdf !== 'HKDF-SHA-256' ||
        envelope.network !== 'mainnet'
    ) {
        throw new Error('Unsupported recovery envelope format')
    }
    assertIdentity(envelope)
    if (expectedIdentity) {
        assertIdentity(expectedIdentity)
        if (
            envelope.userId !== expectedIdentity.userId ||
            envelope.paymentAddress !== expectedIdentity.paymentAddress ||
            envelope.ordinalsAddress !== expectedIdentity.ordinalsAddress
        ) {
            throw new Error('Recovery backup does not match this wallet')
        }
    }
    if (expectedCredentialId && envelope.credentialId !== expectedCredentialId) {
        throw new Error('Recovery backup does not match this passkey')
    }
    if (envelope.walletGeneration !== (await walletRecoveryGeneration(envelope))) {
        throw new Error('Recovery backup wallet generation is invalid')
    }
    decodeBase64Url(envelope.credentialId, 1, 1023)
    decodeBase64Url(envelope.prfSalt, PRF_BYTES, PRF_BYTES)
    decodeBase64Url(envelope.hkdfSalt, PRF_BYTES, PRF_BYTES)
    decodeBase64Url(envelope.walletNonce, NONCE_BYTES, NONCE_BYTES)
    decodeBase64Url(envelope.wrappedKeyNonce, NONCE_BYTES, NONCE_BYTES)
    decodeBase64Url(envelope.ciphertext, TAG_BYTES + 1, WALLET_MAX_BYTES + TAG_BYTES)
    decodeBase64Url(envelope.wrappedKey, PRF_BYTES + TAG_BYTES, PRF_BYTES + TAG_BYTES)
    return envelope
}

/**
 * Stable wallet AAD permits another credential to wrap the same DEK in a future version.
 * The wrapping AAD also authenticates every credential-specific envelope parameter.
 */
function additionalData(envelope: WalletPasskeyEnvelope, purpose: string): Uint8Array {
    const fields: (string | number)[] = [
        purpose,
        envelope.version,
        envelope.algorithm,
        envelope.kdf,
        envelope.network,
        envelope.userId,
        envelope.paymentAddress,
        envelope.ordinalsAddress,
        envelope.walletGeneration,
    ]
    if (purpose === WRAP_CONTEXT) {
        fields.push(
            envelope.credentialId,
            envelope.prfSalt,
            envelope.hkdfSalt,
            envelope.walletNonce,
            envelope.wrappedKeyNonce,
        )
    }
    return encoder.encode(JSON.stringify(fields))
}

async function deriveWrappingKey(prfOutput: Uint8Array, hkdfSalt: string): Promise<CryptoKey> {
    if (!(prfOutput instanceof Uint8Array) || prfOutput.length !== PRF_BYTES) {
        throw new Error('Passkey did not provide a valid PRF secret')
    }
    const subtle = webCrypto().subtle
    const material = await subtle.importKey('raw', prfOutput, 'HKDF', false, ['deriveKey'])
    return subtle.deriveKey(
        {
            name: 'HKDF',
            hash: 'SHA-256',
            salt: decodeBase64Url(hkdfSalt, PRF_BYTES, PRF_BYTES),
            info: encoder.encode(WRAP_CONTEXT),
        },
        material,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt'],
    )
}

function aesParameters(envelope: WalletPasskeyEnvelope, wrapping: boolean): AesGcmParams {
    return {
        name: 'AES-GCM',
        iv: decodeBase64Url(wrapping ? envelope.wrappedKeyNonce : envelope.walletNonce, 12, 12),
        additionalData: additionalData(envelope, wrapping ? WRAP_CONTEXT : WALLET_CONTEXT),
        tagLength: 128,
    }
}

export async function createWalletPasskeyEnvelope({
    walletText,
    prfOutput,
    credentialId,
    prfSalt,
    identity,
}: {
    walletText: string
    prfOutput: Uint8Array
    credentialId: string
    prfSalt: string
    identity: WalletRecoveryIdentity
}): Promise<WalletPasskeyEnvelope> {
    assertIdentity(identity)
    validateWalletTextForRecovery(walletText, identity)
    decodeBase64Url(credentialId, 1, 1023)
    decodeBase64Url(prfSalt, PRF_BYTES, PRF_BYTES)
    const walletBytes = encoder.encode(walletText)
    if (walletBytes.length > WALLET_MAX_BYTES) throw new Error('Wallet backup is too large')
    const crypto = webCrypto()
    const envelope: WalletPasskeyEnvelope = {
        version: 1,
        algorithm: 'AES-256-GCM',
        kdf: 'HKDF-SHA-256',
        network: 'mainnet',
        userId: identity.userId,
        paymentAddress: identity.paymentAddress,
        ordinalsAddress: identity.ordinalsAddress,
        walletGeneration: await walletRecoveryGeneration(identity),
        credentialId,
        prfSalt,
        hkdfSalt: createWalletPasskeyPrfSalt(),
        walletNonce: encodeBase64Url(crypto.getRandomValues(new Uint8Array(NONCE_BYTES))),
        wrappedKeyNonce: encodeBase64Url(crypto.getRandomValues(new Uint8Array(NONCE_BYTES))),
        ciphertext: '',
        wrappedKey: '',
    }
    const dek = crypto.getRandomValues(new Uint8Array(PRF_BYTES))
    try {
        const key = await crypto.subtle.importKey('raw', dek, 'AES-GCM', false, ['encrypt'])
        const wrappingKey = await deriveWrappingKey(prfOutput, envelope.hkdfSalt)
        envelope.ciphertext = encodeBase64Url(
            new Uint8Array(
                await crypto.subtle.encrypt(aesParameters(envelope, false), key, walletBytes),
            ),
        )
        envelope.wrappedKey = encodeBase64Url(
            new Uint8Array(
                await crypto.subtle.encrypt(aesParameters(envelope, true), wrappingKey, dek),
            ),
        )
        return envelope
    } finally {
        dek.fill(0)
        walletBytes.fill(0)
    }
}

export async function openWalletPasskeyEnvelope(
    value: unknown,
    prfOutput: Uint8Array,
    expectedIdentity: WalletRecoveryIdentity,
    expectedCredentialId?: string,
): Promise<string> {
    assertIdentity(expectedIdentity)
    const envelope = await validateWalletPasskeyEnvelope(
        value,
        expectedIdentity,
        expectedCredentialId,
    )
    const subtle = webCrypto().subtle
    const wrappingKey = await deriveWrappingKey(prfOutput, envelope.hkdfSalt)
    let dek: Uint8Array | undefined
    let plaintext: Uint8Array | undefined
    try {
        dek = new Uint8Array(
            await subtle.decrypt(
                aesParameters(envelope, true),
                wrappingKey,
                decodeBase64Url(envelope.wrappedKey, 48, 48),
            ),
        )
        if (dek.length !== PRF_BYTES) throw new Error('Invalid backup encryption key')
        const key = await subtle.importKey('raw', dek, 'AES-GCM', false, ['decrypt'])
        plaintext = new Uint8Array(
            await subtle.decrypt(
                aesParameters(envelope, false),
                key,
                decodeBase64Url(envelope.ciphertext),
            ),
        )
        const walletText = decoder.decode(plaintext)
        validateWalletTextForRecovery(walletText, expectedIdentity)
        return walletText
    } catch {
        // Never include wallet text, credential material, or provider error data.
        throw new Error('Passkey could not decrypt a backup matching this wallet')
    } finally {
        dek?.fill(0)
        plaintext?.fill(0)
    }
}
